import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useResetPassword } from '@/features/auth/hooks/useAuthMutations';
import { AuthCardLayout } from './../components/AuthCardLayout';
import { AuthForm } from './../components/AuthForm';

/**
 * ResetPasswordPage — consumes the `?token=…` from the email link.
 *
 * `token` is required by the API; if the URL is missing it, we
 * render a "missing token" placeholder rather than an empty form.
 * The mutation is invoked with `{ token, newPassword }` and on
 * success the toast says "listo, volvé a iniciar sesión".
 */
const resetSchema = z
  .object({
    newPassword: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Las contraseñas no coinciden',
  });

type ResetValues = { newPassword: string; confirmPassword: string };

const RESET_FIELDS = [
  {
    name: 'newPassword' as const,
    def: {
      name: 'newPassword',
      label: 'Nueva contraseña',
      type: 'password' as const,
      autoComplete: 'new-password',
      required: true,
    },
  },
  {
    name: 'confirmPassword' as const,
    def: {
      name: 'confirmPassword',
      label: 'Confirmar contraseña',
      type: 'password' as const,
      autoComplete: 'new-password',
      required: true,
    },
  },
];

export function ResetPasswordPage(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const reset = useResetPassword();

  if (!token) {
    return (
      <AuthCardLayout
        title="Enlace inválido"
        subtitle="La URL no incluye un token de recuperación"
        footer={
          <Link
            to="/forgot-password"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Solicitar uno nuevo
          </Link>
        }
      >
        <p className="text-sm text-muted-foreground">
          Para restablecer tu contraseña empezá desde la pantalla de "¿olvidaste tu contraseña?" y
          copiá el enlace que se muestra en pantalla.
        </p>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout
      title="Crear nueva contraseña"
      subtitle="Elegí una contraseña que no hayas usado antes"
      footer={
        <Link
          to="/login"
          className="font-medium text-foreground underline-offset-4 hover:underline"
        >
          Volver a iniciar sesión
        </Link>
      }
    >
      <AuthForm<ResetValues>
        schema={resetSchema}
        fields={RESET_FIELDS}
        defaults={{ newPassword: '', confirmPassword: '' }}
        submitting={reset.isPending}
        submitLabel="Actualizar contraseña"
        onValidSubmit={async (values) => {
          await reset.mutateAsync({ token, newPassword: values.newPassword });
        }}
      />
    </AuthCardLayout>
  );
}
